import {
  useState,
  useRef,
  forwardRef,
  useId,
  useEffect,
  type JSX,
} from 'react';
import {
  Swiper,
  SwiperSlide,
  SwiperProps,
  SwiperRef,
  SwiperClass,
} from 'swiper/react';
import {
  Navigation,
  Pagination,
  Mousewheel,
  Thumbs,
  A11y,
} from 'swiper/modules';
import { useTranslations } from 'next-intl';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { NavigationOptions } from 'swiper/types';

enum ButtonSizes {
  md = 'md',
  lg = 'lg',
}

interface ICarouselProps extends SwiperProps {
  hasGradient?: boolean;
  hasCustomNagivation?: boolean;
  customNavigationClasses?: string;
  /**
   * Because this component is using CSS files, it causes problem duplicating it,
   * That's why we use this prop.
   */
  hasNavigationV2?: boolean;
  headerTitle?: React.ReactNode;
  ariaLabel?: string | null;
  card?: JSX.Element;
  buttonSize?: ButtonSizes;
  UNSAFE_className?: string;
}

const Carousel = forwardRef<SwiperRef, ICarouselProps>(
  (
    {
      hasGradient = false,
      hasCustomNagivation = false,
      customNavigationClasses,
      hasNavigationV2 = false,
      headerTitle,
      ariaLabel,
      card,
      buttonSize = ButtonSizes.md,
      UNSAFE_className = '',
      ...props
    },
    swiperRef,
  ) => {
    const id = useId();

    if (hasCustomNagivation && hasNavigationV2) {
      throw new Error(
        'Pick either one of `hasCustomNagivation` or `hasNavigationV2`.',
      );
    }

    const tActions = useTranslations('Actions');
    const [swiper, setSwiper] = useState<SwiperClass | null>(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const navigationNextRef = useRef<HTMLButtonElement>(null);
    const navigationPrevRef = useRef<HTMLButtonElement>(null);
    const allyOptions = {
      prevSlideMessage: tActions('previousElement'),
      nextSlideMessage: tActions('nextElement'),
    };

    useEffect(() => {
      // Will works for elements that appears after Swiper component

      if (
        swiper &&
        hasNavigationV2 &&
        navigationNextRef.current &&
        navigationPrevRef.current
      ) {
        swiper.navigation.nextEl = navigationNextRef.current;
        swiper.navigation.prevEl = navigationPrevRef.current;
      }
    }, []);

    const swiperPropsWithGradientHandlingEvents: SwiperProps = {
      ...props,
      ...((hasCustomNagivation || hasNavigationV2) && {
        // Required to disable swiper's default navigation
        // even though our custom elements aren't initialised yet.
        navigation: {
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        },
        onBeforeInit: (swiper) => {
          // Will works for elements that appears before Swiper component

          (swiper.params.navigation as NavigationOptions).nextEl =
            navigationNextRef.current;
          (swiper.params.navigation as NavigationOptions).prevEl =
            navigationPrevRef.current;
        },
      }),
      ...(hasGradient && {
        onSlideChange: ({ isBeginning, isEnd }): void => {
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        },
        onReachEnd: ({ isBeginning, isEnd }): void => {
          setIsEnd(isEnd);
          setIsBeginning(isBeginning);
        },
        onReachBeginning: ({ isBeginning, isEnd }): void => {
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        },
      }),
    };

    const hasGradientStyle = `
      tw-relative
      after:tw-content-[""]
      after:tw-pointer-events-none
      after:tw-absolute
      after:tw-h-full
      after:tw-bg-gradient-to-r
      after:tw-via-transparent after:tw-via-[length:45px_calc(100%_-_45px)]
      ${isBeginning ? 'after:tw-from-transparent' : 'after:tw-from-white'}
      ${isEnd ? 'after:tw-to-transparent' : 'after:tw-to-white'}
      after:tw-left-[-1px] after:tw-top-0 after:tw-right-[-1px]
      [& .swiper:before]:tw-hidden
      [&_.swiper-button-prev]:!tw-z-10
      [&_.swiper-button-next]:!tw-z-10
    `;

    return (
      <section
        className={`lf-carousel tw-relative ${
          props.slidesPerView === 'auto' ? 'lf-slide-size-auto' : ''
        }
        lf-carousel-button-${buttonSize} ${UNSAFE_className}
        ${hasNavigationV2 ? 'lf-carousel-v2' : ''}`}
        aria-roledescription="carousel"
        aria-label={ariaLabel ?? undefined}
        aria-labelledby={!ariaLabel && headerTitle ? `${id}-title` : undefined}
      >
        {(headerTitle || hasCustomNagivation) && (
          <div className="tw-flex">
            {headerTitle && (
              <div id={`${id}-title`} className="tw-grow desktop:tw-pr-6">
                {headerTitle}
              </div>
            )}
            {hasCustomNagivation && (
              <div
                className={
                  customNavigationClasses
                    ? customNavigationClasses
                    : 'nav-arrows-container tw-relative tw-flex tw-w-20 tw-shrink-0 tw-justify-end'
                }
              >
                <button
                  ref={navigationPrevRef}
                  type="button"
                  className={`
                  swiper-button-prev !tw-left-0
                  tw-top-0
                  tw-hidden !tw-shadow-none
                  desktop:tw-block
                  ${isBeginning ? 'swiper-button-disabled' : ''}`}
                  aria-label={tActions('previous')}
                  data-testid="Carousel-button-previous"
                />
                <button
                  ref={navigationNextRef}
                  type="button"
                  className={`swiper-button-next tw-absolute
                  !tw-right-0
                  tw-top-0 tw-hidden
                  desktop:tw-block ${isEnd ? 'swiper-button-disabled' : ''}`}
                  aria-label={tActions('next')}
                  data-testid="Carousel-button-next"
                />
              </div>
            )}
          </div>
        )}

        <div className="carousel-and-card-wrapper tw-flex">
          {card}
          <Swiper
            ref={swiperRef}
            onSwiper={setSwiper}
            modules={[Navigation, Pagination, Thumbs, A11y, Mousewheel]}
            a11y={allyOptions}
            {...swiperPropsWithGradientHandlingEvents}
            className={`${
              hasGradient ? hasGradientStyle : ''
            } tw-min-w-0 tw-min-w-0 tw-overflow-clip`}
          />
          {hasNavigationV2 && (
            <>
              <button
                ref={navigationPrevRef}
                type="button"
                className={`
              swiper-button-prev
              tw-top-0
              tw-hidden !tw-shadow-none
              desktop:tw-block
              ${isBeginning ? 'swiper-button-disabled' : ''}`}
                aria-label={tActions('previous')}
                aria-disabled={isBeginning}
                data-testid="Carousel-button-previous"
              />
              <button
                ref={navigationNextRef}
                type="button"
                className={`swiper-button-next tw-absolute
              tw-top-0 tw-hidden
              desktop:tw-block ${isEnd ? 'swiper-button-disabled' : ''}`}
                aria-label={tActions('next')}
                aria-disabled={isEnd}
                data-testid="Carousel-button-next"
              />
            </>
          )}
        </div>
      </section>
    );
  },
);

export {
  Carousel,
  SwiperSlide as CarouselSlide,
  Swiper as CarouselSwiper,
  ButtonSizes,
};
